import type { ResumeDto, UserDto } from "@reactive-resume/dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";

// Types for admin responses
export type AdminUserDto = UserDto & {
  role: "WORKER" | "MANAGER" | "ADMIN";
  jobTitle?: string | null;
  department?: string | null;
  skills: string[];
  _count?: { resumes: number };
  resumes?: ResumeDto[];
};

export type PaginatedResponse<T> = {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

export type AdminStats = {
  totalUsers: number;
  totalResumes: number;
  usersByRole: { role: string; count: number }[];
  usersByDepartment: { department: string | null; count: number }[];
};

// Admin Stats
export const fetchAdminStats = async () => {
  const response = await axios.get<AdminStats, AxiosResponse<AdminStats>>("/admin/stats");
  return response.data;
};

export const useAdminStats = () => {
  const { error, isPending: loading, data: stats } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: fetchAdminStats,
  });
  return { stats, loading, error };
};

// Admin Users
export const fetchAdminUsers = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  department?: string;
  role?: string;
}) => {
  const response = await axios.get<
    PaginatedResponse<AdminUserDto>,
    AxiosResponse<PaginatedResponse<AdminUserDto>>
  >("/admin/users", { params });
  return response.data;
};

export const useAdminUsers = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  department?: string;
  role?: string;
}) => {
  const { error, isPending: loading, data } = useQuery({
    queryKey: ["admin", "users", params],
    queryFn: () => fetchAdminUsers(params),
  });
  return { users: data?.data, meta: data?.meta, loading, error };
};

// Fetch single user with resumes
export const fetchAdminUser = async (id: string) => {
  const response = await axios.get<AdminUserDto, AxiosResponse<AdminUserDto>>(`/admin/users/${id}`);
  return response.data;
};

export const useAdminUser = (id: string) => {
  const { error, isPending: loading, data: user } = useQuery({
    queryKey: ["admin", "users", id],
    queryFn: () => fetchAdminUser(id),
    enabled: !!id,
  });
  return { user, loading, error };
};

// Update user
export const updateAdminUser = async (
  id: string,
  data: { role?: string; jobTitle?: string; department?: string; skills?: string[] },
) => {
  const response = await axios.patch<AdminUserDto, AxiosResponse<AdminUserDto>>(
    `/admin/users/${id}`,
    data,
  );
  return response.data;
};

export const useUpdateAdminUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateAdminUser>[1] }) =>
      updateAdminUser(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

// Create user
export type CreateUserDto = {
  name: string;
  email: string;
  username: string;
  password: string;
  role?: "WORKER" | "MANAGER" | "ADMIN";
  jobTitle?: string;
  department?: string;
  skills?: string[];
};

export const createAdminUser = async (data: CreateUserDto) => {
  const response = await axios.post<AdminUserDto, AxiosResponse<AdminUserDto>>("/admin/users", data);
  return response.data;
};

export const useCreateAdminUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserDto) => createAdminUser(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
  });
};

// Delete user
export const deleteAdminUser = async (id: string) => {
  const response = await axios.delete<{ success: boolean }, AxiosResponse<{ success: boolean }>>(
    `/admin/users/${id}`,
  );
  return response.data;
};

export const useDeleteAdminUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAdminUser(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
  });
};

// Admin Resumes
export const fetchAdminResumes = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  userId?: string;
}) => {
  const response = await axios.get<
    PaginatedResponse<ResumeDto & { user: AdminUserDto }>,
    AxiosResponse<PaginatedResponse<ResumeDto & { user: AdminUserDto }>>
  >("/admin/resumes", { params });
  return response.data;
};

export const useAdminResumes = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  userId?: string;
}) => {
  const { error, isPending: loading, data } = useQuery({
    queryKey: ["admin", "resumes", params],
    queryFn: () => fetchAdminResumes(params),
  });
  return { resumes: data?.data, meta: data?.meta, loading, error };
};

// Fetch single resume
export const fetchAdminResume = async (id: string) => {
  const response = await axios.get<
    ResumeDto & { user: AdminUserDto },
    AxiosResponse<ResumeDto & { user: AdminUserDto }>
  >(`/admin/resumes/${id}`);
  return response.data;
};

export const useAdminResume = (id: string) => {
  const { error, isPending: loading, data: resume } = useQuery({
    queryKey: ["admin", "resumes", id],
    queryFn: () => fetchAdminResume(id),
    enabled: !!id,
  });
  return { resume, loading, error };
};

// Departments
export const fetchDepartments = async () => {
  const response = await axios.get<string[], AxiosResponse<string[]>>("/admin/departments");
  return response.data;
};

export const useDepartments = () => {
  const { error, isPending: loading, data: departments } = useQuery({
    queryKey: ["admin", "departments"],
    queryFn: fetchDepartments,
  });
  return { departments, loading, error };
};

// Create resume for a user (admin functionality)
export type CreateResumeForUserDto = {
  title: string;
  slug: string;
  visibility?: "public" | "private";
};

export const createResumeForUser = async (userId: string, data: CreateResumeForUserDto) => {
  const response = await axios.post<ResumeDto, AxiosResponse<ResumeDto>>(
    `/admin/users/${userId}/resumes`,
    data,
  );
  return response.data;
};

export const useCreateResumeForUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: CreateResumeForUserDto }) =>
      createResumeForUser(userId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", variables.userId] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "resumes"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
  });
};

// Delete resume (admin functionality)
export const deleteAdminResume = async (id: string) => {
  const response = await axios.delete<ResumeDto, AxiosResponse<ResumeDto>>(`/admin/resumes/${id}`);
  return response.data;
};

export const useDeleteAdminResume = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAdminResume(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "resumes"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
  });
};
